import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findIndex, groupBy } from 'lodash'
import {
  EtlFileMappings,
  EtlStatus,
  EtlFileCharacteristic,
  EtlQuestion,
  EtlValueMapping,
  EtlCharValueMapping,
  EtlFinalCheckError,
} from '../../shared/model/models'
import {
  setEtlFiles,
  setLatestEtlStatus,
  setFileMappings,
  getFileMappings,
  getFileCharacteristics,
  setFileCharacteristics,
  getQuestions,
  setQuestions,
  addQuestion,
  getVariables,
  addVariable,
  getOriginalValues,
  goBackETLStep,
  updateVariable,
  deleteVariable,
  deleteCharVariable,
  updateEtlStep,
  getEtlFinalCheckErrors,
  updateEtlError,
  reportEtlErrors,
} from '../actions/etl'

type ETLState = {
  currentStep: number
  etlFiles: Record<string, string[]> | null
  etlFileMappings: Record<string, EtlFileMappings>
  latestEtlStatus: EtlStatus | null
  etlFileCharacteristics: EtlFileCharacteristic[] | null
  etlQuestions: EtlQuestion[] | null
  etlValueMappings: EtlCharValueMapping[] | null
  etlOriginalValues: Record<number, string[]> | null
  etlFinalCheckErrors: EtlFinalCheckError[] | null
}

const initialState: ETLState = {
  etlFiles: null,
  currentStep: 0,
  etlFileMappings: {},
  latestEtlStatus: null,
  etlFileCharacteristics: null,
  etlQuestions: null,
  etlValueMappings: null,
  etlOriginalValues: null,
  etlFinalCheckErrors: null,
}

const etlSlice = createSlice({
  name: 'etls',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload
    },
    setEtlFileMappings: (state, action: PayloadAction<Record<string, EtlFileMappings>>) => {
      state.etlFileMappings = action.payload
    },
    resetEtl: (state) => {
      Object.assign(state, initialState)
    },
    updateFileCharacteristics: (
      state,
      action: PayloadAction<{ id: number; value: string | null; type: 'file' | 'column' }>,
    ) => {
      const { id, value, type } = action.payload

      const charToUpdate = state.etlFileCharacteristics?.find((q) => q.id === id)

      if (charToUpdate && type === 'file') {
        charToUpdate.userFileName = value
      } else if (charToUpdate && type === 'column') {
        charToUpdate.userColumnName = value
      }
    },
    setEtlValueMappings: (state, action: PayloadAction<EtlCharValueMapping[]>) => {
      state.etlValueMappings = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      setEtlFiles.fulfilled,
      (state, action: PayloadAction<Record<string, string[]> | null>) => {
        if (action.payload) state.etlFiles = action.payload
      },
    )
    builder.addCase(
      setLatestEtlStatus.fulfilled,
      (state, action: PayloadAction<EtlStatus | null>) => {
        if (action.payload && action.payload.runAt !== null) state.latestEtlStatus = action.payload
      },
    )
    builder.addCase(setFileMappings.fulfilled, (state, action: PayloadAction<EtlStatus | null>) => {
      if (action.payload && !action.payload.status) state.latestEtlStatus = action.payload
    })
    builder.addCase(getFileMappings.fulfilled, (state, action: PayloadAction<any>) => {
      if (action.payload) {
        const files = action.payload
        state.etlFileMappings = {}
        if (files.length === 1) {
          state.etlFileMappings[`${files[0].filename}`] = {
            primaryKey: files[0].primaryKey,
            referenceFile: files[0].referenceFile,
            foreignKey: files[0].foreignKey,
          }
        } else if (files.length === 2) {
          state.etlFileMappings[`${files[0].filename}`] = {
            primaryKey: files[0].primaryKey,
            referenceFile: files[1].filename,
            foreignKey: files[0].foreignKey,
          }
          state.etlFileMappings[`${files[1].filename}`] = {
            primaryKey: files[1].primaryKey,
            referenceFile: files[0].filename,
            foreignKey: files[1].foreignKey,
          }
        } else if (files.length > 2) {
          files.forEach((file: any) => {
            state.etlFileMappings[`${file.filename}`] = {
              primaryKey: file.primaryKey,
              referenceFile: file.referenceFile,
              foreignKey: file.foreignKey,
            }
          })
        }
      }
    })
    builder.addCase(
      getFileCharacteristics.fulfilled,
      (state, action: PayloadAction<EtlFileCharacteristic[]>) => {
        if (action.payload)
          state.etlFileCharacteristics = action.payload.sort((a, b) => a.id - b.id)
      },
    )
    builder.addCase(
      setFileCharacteristics.fulfilled,
      (state, action: PayloadAction<EtlStatus | null>) => {
        if (action.payload && !action.payload.status) state.latestEtlStatus = action.payload
      },
    )
    builder.addCase(getQuestions.fulfilled, (state, action: PayloadAction<EtlQuestion[]>) => {
      state.etlQuestions = action.payload.sort((a, b) => a.id - b.id)
    })
    builder.addCase(
      setQuestions.fulfilled,
      (state, action: PayloadAction<EtlQuestion[] | null>) => {
        if (action.payload) state.etlQuestions = action.payload.sort((a, b) => a.id - b.id)
      },
    )
    builder.addCase(addQuestion.fulfilled, (state, action: PayloadAction<EtlQuestion[]>) => {
      state.etlQuestions = action.payload.sort((a, b) => a.id - b.id)
    })
    builder.addCase(getVariables.fulfilled, (state, action: PayloadAction<EtlValueMapping[]>) => {
      if (action.payload) {
        const grouppedMappings = action.payload
          ? groupBy(action.payload, ({ characteristic }) => characteristic)
          : {}
        const charGroups: EtlCharValueMapping[] = []
        Object.keys(grouppedMappings).forEach((charId) =>
          charGroups.push({
            id: parseInt(charId, 10),
            name:
              state.etlFileCharacteristics?.find((char) => char.id === parseInt(charId, 10))
                ?.name ?? '',
            mappings: grouppedMappings[charId],
          }),
        )
        state.etlValueMappings = charGroups.reverse()
      }
    })
    builder.addCase(
      addVariable.fulfilled,
      (state, action: PayloadAction<EtlValueMapping | null>) => {
        if (action.payload && state.etlValueMappings) {
          const charToUpdate = state.etlValueMappings.findIndex(
            (char) => char.id === action.payload?.characteristic,
          )
          if (charToUpdate !== -1) {
            state.etlValueMappings[charToUpdate].mappings.push(action.payload)
          } else {
            const charId = action.payload?.characteristic ?? 0
            state.etlValueMappings.push({
              id: charId,
              name: state.etlFileCharacteristics?.find((char) => char.id === charId)?.name ?? '',
              mappings: [action.payload],
            })
          }
        }
      },
    )
    builder.addCase(
      updateVariable.fulfilled,
      (state, action: PayloadAction<EtlValueMapping | null>) => {
        if (action.payload && state.etlValueMappings) {
          const charToUpdate = state.etlValueMappings.findIndex(
            (char) => char.id === action.payload?.characteristic,
          )
          if (charToUpdate !== -1) {
            state.etlValueMappings[charToUpdate].mappings = state.etlValueMappings[
              charToUpdate
            ].mappings.map((map) =>
              map.id === action.payload?.id ? { ...map, ...action.payload } : map,
            )
          }
        }
      },
    )
    builder.addCase(deleteVariable.fulfilled, (state, action: PayloadAction<EtlValueMapping>) => {
      if (action.payload && state.etlValueMappings) {
        const charToUpdate = state.etlValueMappings.findIndex(
          (char) => char.id === action.payload.characteristic,
        )
        if (charToUpdate !== -1) {
          state.etlValueMappings[charToUpdate].mappings = state.etlValueMappings[
            charToUpdate
          ].mappings.filter((map) => map.id !== action.payload.id)
        }
      }
    })
    builder.addCase(deleteCharVariable.fulfilled, (state, action: PayloadAction<number>) => {
      if (action.payload && state.etlValueMappings) {
        state.etlValueMappings = state.etlValueMappings.filter((char) => char.id !== action.payload)
      }
    })
    builder.addCase(
      getOriginalValues.fulfilled,
      (state, action: PayloadAction<{ characteristicId: number; values: string[] }>) => {
        if (state.etlOriginalValues)
          state.etlOriginalValues[action.payload.characteristicId] = action.payload.values
        else
          state.etlOriginalValues = {
            [`${action.payload.characteristicId}`]: action.payload.values,
          }
      },
    )
    builder.addCase(updateEtlStep.fulfilled, (state, action: PayloadAction<EtlStatus>) => {
      state.latestEtlStatus = action.payload
    })
    builder.addCase(goBackETLStep.fulfilled, (state, action: PayloadAction<EtlStatus>) => {
      state.latestEtlStatus = action.payload
    })
    builder.addCase(
      getEtlFinalCheckErrors.fulfilled,
      (state, action: PayloadAction<EtlFinalCheckError[]>) => {
        state.etlFinalCheckErrors = action.payload
      },
    )
    builder.addCase(
      updateEtlError.fulfilled,
      (state, action: PayloadAction<EtlFinalCheckError>) => {
        if (state.etlFinalCheckErrors) {
          const errToUpdate = state.etlFinalCheckErrors.findIndex((e) => e.id === action.payload.id)
          if (errToUpdate !== -1) state.etlFinalCheckErrors[errToUpdate] = action.payload
        }
      },
    )
    builder.addCase(
      reportEtlErrors.fulfilled,
      (state, action: PayloadAction<EtlFinalCheckError[]>) => {
        state.etlFinalCheckErrors = action.payload
      },
    )
  },
})

export const etlActions = etlSlice.actions
export default etlSlice.reducer
